import { computed, ref, watch } from 'vue';
import { currentGtfsDate } from '@pysae/transit-core/service-day';
import { defineStore } from 'pinia';
import { useGtfsStore, type Bounds } from './gtfs';
import type { Stop } from '@/@types/gtfs-ressource';
import Common from '@/libs/common';
import { projectFavoriteStops } from '@/libs/helpers/stop-clusters';
import { trackFavoritesUsage } from '@/libs/matomo-events';
import { useGroupStore } from './group';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import i18n from '@/i18n';
import type { Position } from '@capacitor/geolocation';
import type { IconName } from '@/libs/icons';
import { isAppVisible } from '@/libs/app-visibility';
import { datadogLogs } from '@datadog/browser-logs';

const { t } = i18n.global;

const GEOLOCATION_TIMEOUT = 30000;
// A position the phone already holds answers at once: waiting for a fresh fix
// is what made the position so slow to come on Android.
const GEOLOCATION_MAX_AGE = 60000;
const GEOLOCATION_WATCH_INTERVAL = 10000;

export enum MapFocusType {
  BOUNDS,
  STOP,
  STATION,
  GEOLOCATION,
}

export interface AppLoadingState {
  group?: boolean;
  gtfs?: boolean;
  map?: boolean;
}

export interface MapFocus {
  type: MapFocusType;
  elem: Bounds | Stop | Position['coords'];
  noAnimation?: boolean;
  // Re-centre even what is already on screen: an explicit passenger request, not a page opening.
  reframe?: boolean;
}

type Coords = { latitude: number; longitude: number };

export interface LatLong {
  lat: number;
  lon: number;
}

export interface Popup {
  shown?: boolean;
  text: string;
  icon?: IconName;
  timeout?: number;
  reloadGtfsButton?: boolean;
  dismissible?: boolean;
}

export interface Favorites {
  routes: { [key: string]: boolean };
  stops: { [key: string]: boolean };
  // Apart from `routes`: an on-demand route id is not a GTFS one and may share its spelling.
  onDemandRoutes?: { [key: string]: boolean };
  v: number;
}

export const useAppStore = defineStore('app', () => {
  const groupStore = useGroupStore();
  const gtfsStore = useGtfsStore();

  const isLoading = ref<boolean | AppLoadingState>(false);

  const isLoadingAsBoolean = computed<boolean>(() =>
    typeof isLoading.value == 'boolean'
      ? isLoading.value
      : Object.values(isLoading.value).some(loadingValue => loadingValue === true),
  );

  const isSearchBarFocused = ref<boolean>(false);
  const isNativePlateform = ref<boolean>(Capacitor.isNativePlatform());
  const previousPage = ref<string | null>(null);
  const mapFocus = ref<MapFocus>();
  // What the current page frames the map on (network, stop, route or trip): the focus
  // button goes back to it, unlike a geolocation, which only moves the map.
  const contextMapFocus = ref<MapFocus>();
  // Height of the bottom sheet covering the map on the current page, at rest:
  // the map frames its focus in the part left visible above it.
  const mapBottomInset = ref<number>(0);
  /** A point is being picked on the map: a pin stays at its centre while the map moves underneath. */
  const isPickingOnMap = ref<boolean>(false);
  /** The point under that pin, updated as the map comes to rest. */
  const mapPickedPoint = ref<LatLong>();
  const positionGeolocation = ref<LatLong>();
  // Set only on an explicit OS/browser refusal: lets a page tell "refused" from "not asked yet".
  const isGeolocationDenied = ref<boolean>(false);
  let lastPosition: { coords: Coords; at: number } | undefined;
  let geolocationWatch: Promise<string> | undefined;
  // A watch that failed (location turned off, the enable dialog dismissed) is not
  // restarted silently: it would bring the dialog back at every return to the app.
  let isSilentGeolocationBlocked = false;
  const popup = ref<Popup>({ shown: false, text: '' });
  const TOAST_DURATION_MS = 3000;
  const toast = ref<{ message: string; icon?: IconName }>();
  let toastTimeoutId: ReturnType<typeof setTimeout> | undefined;
  const favorites = ref<Favorites>({ routes: {}, stops: {}, v: 1 });
  // The service day of the network, not of the phone: the group timezone, with the group store's fallback.
  const todayDateGtfs = ref<string>(currentGtfsDate(groupStore.group?.tz || 'Europe/Paris'));
  const lastSelectedRouteId = ref<string>(Common.safeLSParse('info.lastSelectedRouteId'));
  const selectedStop = ref<{ devices: string[]; stop: {} }>({
    devices: [],
    stop: {},
  });

  const shouldHideNavbar = computed(() => isSearchBarFocused.value && isNativePlateform.value);

  // Android hardware back button interceptors. Components (e.g. a full-screen
  // bottom sheet) register a handler returning true when they consumed the
  // event; the most recently registered handler wins.
  type BackHandler = () => boolean;
  const backHandlers: BackHandler[] = [];

  function registerBackHandler(handler: BackHandler): () => void {
    backHandlers.push(handler);
    return () => {
      const index = backHandlers.indexOf(handler);
      if (index >= 0) backHandlers.splice(index, 1);
    };
  }

  function handleBackButton(): boolean {
    for (let i = backHandlers.length - 1; i >= 0; i--) {
      if (backHandlers[i]()) return true;
    }
    return false;
  }

  function setLoading(value: AppLoadingState) {
    const newState = Object.assign({}, isLoading.value, value);
    if (Object.keys(newState).every(k => !newState[k as keyof AppLoadingState])) {
      isLoading.value = false;
    } else {
      isLoading.value = newState;
    }
  }

  function resetState() {
    hidePopup();
    mapFocus.value = undefined;
    contextMapFocus.value = undefined;
  }

  function updateToday() {
    todayDateGtfs.value = currentGtfsDate(groupStore.group?.tz || 'Europe/Paris');
  }

  // The group can load or change after the store is created: no need to wait for the periodic refresh.
  watch(() => groupStore.group?.tz, updateToday, { flush: 'sync' });

  function hidePopup() {
    popup.value.shown = false;
  }

  function showPopup(popupToDisplay: Popup) {
    popupToDisplay.shown = true;
    popup.value = popupToDisplay;
  }

  function showToast(message: string, icon?: IconName) {
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    toast.value = { message, icon };
    toastTimeoutId = setTimeout(() => {
      toast.value = undefined;
    }, TOAST_DURATION_MS);
  }

  function loadFavorites() {
    if (!groupStore.group) return;
    const lsFavs: Favorites = Common.safeLSParse('favorites.' + groupStore.group.group_id) || {
      routes: {},
      stops: {},
      v: 1,
    };
    const projectedStops = projectFavoriteStops(lsFavs.stops ?? {}, gtfsStore.canonicalOf);
    const hasMoved = Object.keys(lsFavs.stops ?? {}).some(stopId => !projectedStops[stopId]);
    favorites.value = { ...lsFavs, stops: projectedStops };
    if (hasMoved) saveFavoriteInLS();
    trackFavoritesUsage(groupStore.group.group_id, favorites.value);
  }

  function setLastSelectedRouteId(value: string) {
    lastSelectedRouteId.value = value;
    localStorage.setItem('info.lastSelectedRouteId', value);
  }

  function setSelectedStopDevices(devices: Array<string>) {
    selectedStop.value.devices = devices;
  }

  function setSelectedStopId(stopId: string) {
    selectedStop.value.stop = gtfsStore.gtfsData?.stops?.[stopId] || {};
  }

  // Wired to a BottomSheet's `update:height`. Only resting heights go through:
  // reframing the map on every frame of a drag would fight the finger.
  function setMapBottomInset(height: number, dragging: boolean) {
    if (!dragging) mapBottomInset.value = height;
  }

  function setPickingOnMap(picking: boolean) {
    isPickingOnMap.value = picking;
    if (!picking) mapPickedPoint.value = undefined;
  }

  function setMapPickedPoint(point: LatLong) {
    mapPickedPoint.value = point;
  }

  function saveFavoriteInLS(favoritesTS: Favorites | null = null) {
    const favoritesToSave = favoritesTS || favorites.value;
    if (!groupStore.group) return;
    localStorage.setItem('favorites.' + groupStore.group.group_id, JSON.stringify(favoritesToSave));
  }

  function starRoute(routeId: string) {
    favorites.value.routes[routeId] = true;
    saveFavoriteInLS();
  }

  // A platform merged into another stop is still reachable from the map: its favorite
  // lives on the canonical id, the one the lists show.
  function starStop(stopId: string) {
    favorites.value.stops[gtfsStore.getCanonicalStopId(stopId)] = true;
    saveFavoriteInLS();
  }

  function starOnDemandRoute(routeId: string) {
    favorites.value.onDemandRoutes = { ...favorites.value.onDemandRoutes, [routeId]: true };
    saveFavoriteInLS();
  }

  function unstarOnDemandRoute(routeId: string) {
    if (!favorites.value.onDemandRoutes) return;
    delete favorites.value.onDemandRoutes[routeId];
    saveFavoriteInLS();
  }

  function unstarRoute(routeId: string) {
    delete favorites.value.routes[routeId];
    saveFavoriteInLS();
  }

  function unstarStop(stopId: string) {
    delete favorites.value.stops[gtfsStore.getCanonicalStopId(stopId)];
    saveFavoriteInLS();
  }

  /** Asks for the position, prompting for the permission if needed. `focusMap: false` keeps the page's framing. */
  async function getGeolocation({ focusMap = true }: { focusMap?: boolean } = {}) {
    if (isNativePlateform.value) {
      await getGeolocationNative(focusMap);
    } else {
      await getGeolocationBrowser(focusMap);
    }
  }

  async function getGeolocationNative(focusMap: boolean) {
    try {
      const permissions = await Geolocation.requestPermissions({ permissions: ['location'] });
      isGeolocationDenied.value = permissions.location === 'denied';
      if (permissions.location === 'denied') {
        showPopup({
          text: t('error.geolocationRefused'),
          icon: 'map-pin-off',
        });
        return;
      }
      if (permissions.location !== 'granted') {
        showPopup({
          text: t('error.geolocationDisabled'),
          icon: 'map-pin-off',
        });
        return;
      }
      isSilentGeolocationBlocked = false;
      if (lastPosition && isPositionFresh()) {
        if (focusMap) focusOnPosition(lastPosition.coords);
        startGeolocationWatch();
        return;
      }
      showPopup({
        text: t('common.geolocalizing'),
        timeout: GEOLOCATION_TIMEOUT,
      });
      const position = await getCurrentNativePosition();
      setGeolocation(position.coords, { focusMap });
      hidePopup();
      startGeolocationWatch();
    } catch {
      showPopup({
        text: t('error.geolocationDisabled'),
        icon: 'map-pin-off',
      });
    }
  }

  async function getGeolocationBrowser(focusMap: boolean) {
    if (!('geolocation' in navigator)) {
      showPopup({
        text: t('error.geolocationDisabled'),
        icon: 'map-pin-off',
      });
      return;
    }
    showPopup({
      text: t('common.geolocalizing'),
      timeout: GEOLOCATION_TIMEOUT,
    });
    return new Promise<void>(resolve => {
      navigator.geolocation.getCurrentPosition(
        position => {
          setGeolocation(position.coords, { focusMap });
          hidePopup();
          resolve();
        },
        error => {
          if (error.code === error.PERMISSION_DENIED) {
            isGeolocationDenied.value = true;
            showPopup({
              text: t('error.geolocationRefused'),
              icon: 'map-pin-off',
            });
          } else {
            showPopup({
              text: t('error.geolocationDisabled'),
              icon: 'map-pin-off',
            });
          }
          resolve();
        },
        { timeout: GEOLOCATION_TIMEOUT },
      );
    });
  }

  /**
   * Try to fetch user position only if permission is already granted.
   * Does not prompt the user, display any popup nor move the map: a page calls
   * it for the position itself (nearby stops), and keeps its own framing.
   */
  async function getGeolocationIfAllowed() {
    try {
      if (isNativePlateform.value) {
        const permissions = await Geolocation.checkPermissions();
        isGeolocationDenied.value = permissions.location === 'denied';
        if (permissions.location !== 'granted' || isSilentGeolocationBlocked) return;
        startGeolocationWatch();
        if (isPositionFresh()) return;
        const position = await getCurrentNativePosition();
        setGeolocation(position.coords, { focusMap: false });
        return;
      }

      if (!('geolocation' in navigator)) return;
      if ('permissions' in navigator) {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        isGeolocationDenied.value = status.state === 'denied';
        if (status.state !== 'granted') return;
      } else {
        // No Permissions API: skip silent fetch to avoid surprise prompt.
        return;
      }
      navigator.geolocation.getCurrentPosition(
        position => setGeolocation(position.coords, { focusMap: false }),
        () => {},
        { timeout: GEOLOCATION_TIMEOUT },
      );
    } catch {
      // Silent: this is a best-effort background check.
    }
  }

  /**
   * Asked once at launch: a permission already answered waits for a feature that needs the position.
   * Resolves once the dialog is answered, not with the position, which may take its full timeout.
   */
  async function requestGeolocationAtLaunch() {
    if (!isNativePlateform.value) return;
    try {
      const permissions = await Geolocation.checkPermissions();
      if (permissions.location === 'prompt') {
        await Geolocation.requestPermissions({ permissions: ['location'] });
      }
    } catch {
      // The request at a feature tap still asks.
    }
    getGeolocationIfAllowed();
  }

  function isPositionFresh() {
    return !!lastPosition && Date.now() - lastPosition.at < GEOLOCATION_MAX_AGE;
  }

  async function getCurrentNativePosition(): Promise<Position> {
    const startedAt = Date.now();
    const position = await Geolocation.getCurrentPosition({
      timeout: GEOLOCATION_TIMEOUT,
      enableHighAccuracy: false,
      maximumAge: GEOLOCATION_MAX_AGE,
    });
    logPositionDelay(startedAt, 'current', position.coords);
    return position;
  }

  /** Follows the position while the app is on screen, so that a page asking for it gets it at once. */
  function startGeolocationWatch() {
    if (geolocationWatch || !isAppVisible.value) return;
    const startedAt = Date.now();
    let isFirstPosition = true;
    const pendingWatch = Geolocation.watchPosition(
      {
        enableHighAccuracy: false,
        timeout: GEOLOCATION_TIMEOUT,
        maximumAge: GEOLOCATION_MAX_AGE,
        interval: GEOLOCATION_WATCH_INTERVAL,
      },
      (position, error) => {
        if (error || !position) {
          // A late fix is no reason to stop asking; anything else is (see isSilentGeolocationBlocked).
          if (error?.code !== 'OS-PLUG-GLOC-0010') isSilentGeolocationBlocked = true;
          stopGeolocationWatch();
          return;
        }
        if (isFirstPosition) logPositionDelay(startedAt, 'watch', position.coords);
        isFirstPosition = false;
        setGeolocation(position.coords, { focusMap: false });
      },
    );
    geolocationWatch = pendingWatch;
    pendingWatch.catch(() => {
      if (geolocationWatch === pendingWatch) geolocationWatch = undefined;
    });
  }

  async function stopGeolocationWatch() {
    const pendingWatch = geolocationWatch;
    geolocationWatch = undefined;
    if (!pendingWatch) return;
    try {
      await Geolocation.clearWatch({ id: await pendingWatch });
    } catch {
      // Already gone on the native side.
    }
  }

  watch(isAppVisible, visible => {
    if (!isNativePlateform.value) return;
    if (visible) getGeolocationIfAllowed();
    else stopGeolocationWatch();
  });

  function logPositionDelay(startedAt: number, source: 'current' | 'watch', coords: Position['coords']) {
    datadogLogs.logger.info('Geolocation position obtained', {
      geolocation: {
        delay_ms: Date.now() - startedAt,
        platform: Capacitor.getPlatform(),
        source,
        accuracy_m: Math.round(coords.accuracy),
      },
    });
  }

  /** The framing of the current page, which the focus button restores. */
  function setMapFocus(focus: MapFocus) {
    contextMapFocus.value = focus;
    mapFocus.value = focus;
  }

  /** Focus button: back to the current page's framing, or the whole network where it has none. */
  function refocusMap() {
    const focus = contextMapFocus.value ?? {
      type: MapFocusType.BOUNDS,
      elem: gtfsStore.networkMapBounds,
    };
    mapFocus.value = { ...focus, noAnimation: false, reframe: true };
  }

  // `focusMap` brings the map to the position: for a request the passenger just
  // made, never for a silent refresh, which would steal the page's framing.
  function setGeolocation(coords: Coords, { focusMap }: { focusMap: boolean }) {
    if (focusMap) focusOnPosition(coords);
    lastPosition = { coords, at: Date.now() };
    isGeolocationDenied.value = false;
    positionGeolocation.value = {
      lat: coords.latitude,
      lon: coords.longitude,
    };
  }

  function focusOnPosition(coords: Coords) {
    mapFocus.value = { type: MapFocusType.GEOLOCATION, elem: coords as Position['coords'] };
  }

  return {
    isLoading,
    isLoadingAsBoolean,
    isSearchBarFocused,
    isNativePlateform,
    shouldHideNavbar,
    todayDateGtfs,
    previousPage,
    popup,
    toast,
    favorites,
    mapFocus,
    mapBottomInset,
    isPickingOnMap,
    mapPickedPoint,
    setPickingOnMap,
    setMapPickedPoint,
    positionGeolocation,
    isGeolocationDenied,
    selectedStop,
    registerBackHandler,
    handleBackButton,
    updateToday,
    setLoading,
    resetState,
    hidePopup,
    loadFavorites,
    showPopup,
    showToast,
    getGeolocationIfAllowed,
    requestGeolocationAtLaunch,
    starRoute,
    starStop,
    starOnDemandRoute,
    unstarOnDemandRoute,
    unstarRoute,
    unstarStop,
    getGeolocation,
    setLastSelectedRouteId,
    setSelectedStopDevices,
    setSelectedStopId,
    setMapBottomInset,
    setMapFocus,
    refocusMap,
  };
});
